import { FieldsCardBody } from "@platform/design-system";

import { UpdateRfpDetailsRequest } from "../../../../../../src/libs/frontend/coverage-overview/src/lib/components/SendRfpDrawer/api/types";

import type { RfpDetailsResponse } from "../../../types/rfpApi";

// broker_analyst options share the Assigned Analyst dropdown with Centro analysts.
// Their option value is the broker_analyst's email, prefixed so it serializes as a
// non-numeric string and routes to assignedBrokerAnalystEmail rather than
// primaryAnalystId. See ADR 10.19 amendment.
export const BROKER_ANALYST_OPTION_PREFIX = "broker_analyst:";

export const toBrokerAnalystOptionValue = (email: string): string =>
  `${BROKER_ANALYST_OPTION_PREFIX}${email}`;

type AssignedAnalystUpdate = Pick<
  UpdateRfpDetailsRequest,
  "primaryAnalystId" | "assignedBrokerAnalystEmail"
>;

// The Assigned Analyst dropdown maps to a single body value: a number for a Centro
// analyst, or a prefixed email string for a broker_analyst.
export const parseAssignedAnalyst = (
  value: FieldsCardBody[string],
): AssignedAnalystUpdate => {
  if (typeof value === "number") {
    return { primaryAnalystId: value };
  }
  if (
    typeof value === "string" &&
    value.startsWith(BROKER_ANALYST_OPTION_PREFIX)
  ) {
    return {
      assignedBrokerAnalystEmail: value.slice(
        BROKER_ANALYST_OPTION_PREFIX.length,
      ),
    };
  }
  return {};
};

export type SelectOption = { label: string; value: string };

// The Assigned Analyst slot is filled by either a Centro analyst or a
// broker_analyst (Auth0-sourced). Both populate one dropdown.
export type AssignedAnalystField = {
  options: SelectOption[];
  inputValue: string;
  displayName: string | undefined;
  email: string | undefined;
};

export const buildAssignedAnalystField = (
  resp: RfpDetailsResponse,
  analystOptions: SelectOption[],
  secondaryAnalystId: string,
): AssignedAnalystField => {
  // A broker_analyst removed from Auth0 can come back with only an email, so fall
  // back to the email for the label/display name rather than showing a blank.
  const brokerAnalystOptions = resp.brokerAnalystsList.map((ba) => ({
    label: ba.name || ba.email,
    value: toBrokerAnalystOptionValue(ba.email),
  }));

  return {
    options: [
      ...analystOptions.filter((a) => a.value !== secondaryAnalystId),
      ...brokerAnalystOptions,
    ],
    inputValue: resp.assignedBrokerAnalyst
      ? toBrokerAnalystOptionValue(resp.assignedBrokerAnalyst.email)
      : resp.primaryAnalyst?.id.toString() || "",
    displayName: resp.assignedBrokerAnalyst
      ? resp.assignedBrokerAnalyst.name || resp.assignedBrokerAnalyst.email
      : resp.primaryAnalyst?.name,
    email: resp.assignedBrokerAnalyst
      ? resp.assignedBrokerAnalyst.email
      : resp.primaryAnalyst?.email,
  };
};

type DateValidator = (field: { value: unknown }) => string | undefined;

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !isNaN(value.getTime());

export const validateRevisedDueDate =
  (receivedDate: Date | null): DateValidator =>
  (field) => {
    if (
      receivedDate &&
      isValidDate(field.value) &&
      startOfDay(field.value) < startOfDay(receivedDate)
    ) {
      return "Revised Due Date can't be before the RFP Received Date";
    }
    return undefined;
  };

export const validateNotBeforeToday: DateValidator = (field) => {
  if (
    isValidDate(field.value) &&
    startOfDay(field.value) < startOfDay(new Date())
  ) {
    return "Carrier Due Date must be today or later";
  }
  return undefined;
};
